import React, {
  useCallback, useEffect, useMemo, useReducer,
} from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { ToastContext } from '../context';
import toastReducer from '../reducers/toasts';
import { addToast, removeToast } from '../actions/toasts';
import Toast from '../componentsV2/Toast/Toast';

const INIT_STATE = [];
const TOAST_DURATION = 3000; // 3 seconds

export const ToastProvider = ({ children }) => {
  const [toasts, dispatch] = useReducer(toastReducer, INIT_STATE);

  /**
   * This effect will remove a toast from stack until we need manually close the toast
   */
  useEffect(() => {
    if (toasts.length) {
      const lastToast = toasts[toasts.length - 1];

      if (lastToast.duration > 0) {
        const timer = setTimeout(() => {
          dispatch(removeToast(lastToast.id));
        }, lastToast.duration);

        return () => clearTimeout(timer);
      }
    }
    // when toast.duration ===0 we need manually close the toast
    return undefined;
  }, [toasts]);

  /**
   * Add toast
   * @param {object} toast
   * @param {number} duration allows to set a custom duration for the toast, if the duration 0 we need manually close the toast
   * @param {string} id allows to set a custom id for the toast
   */
  const showToast = useCallback((toast, duration = toast.type === 'error' ? 0 : TOAST_DURATION, id) => {
    dispatch(addToast({
      id: id || uuidv4(),
      duration,
      ...toast,
    }));
  }, []);

  /**
   * Hide toast by id
   * @param {string} id if we pass the id we can hide the toast manually ex.ReportPageWrapper.js
   */
  const hideToast = useCallback((id) => {
    dispatch(removeToast(id));
  }, []);

  const provider = useMemo(() => ({ showToast, hideToast }), [showToast, hideToast]);
  return (
    <ToastContext.Provider value={provider}>
      {children}
      <div className="fixed box-border w-screen top-7 inset-x-0 flex flex-col items-center z-100">
        { toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            isSpinner={toast.isSpinner}
            onClose={() => hideToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

ToastProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

